import { useState, useEffect, useCallback } from "react";
import {
  insertReward,
  listRewards,
  removeReward,
  updateReward,
} from "../api/supabaseData";
import { getCurrentTimestamp } from "../utils/dateUtils";

export function useRewards(uid) {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setRewards([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listRewards(uid)
      .then((rows) => {
        if (!cancelled) {
          setRewards(rows);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load rewards:", error);
          setRewards([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Add new reward
  const addReward = useCallback(async (reward) => {
    const createdReward = await insertReward(uid, reward);
    setRewards((current) => [createdReward, ...current]);
  }, [uid]);

  // Edit reward
  const editReward = useCallback(async (id, updates) => {
    const savedReward = await updateReward(uid, id, updates);
    setRewards((current) => current.map((reward) => (reward.id === id ? savedReward : reward)));
  }, [uid]);

  // Delete reward
  const deleteReward = useCallback(async (id) => {
    await removeReward(uid, id);
    setRewards((current) => current.filter((reward) => reward.id !== id));
  }, [uid]);

  // Claim/undo claim
  const claimReward = useCallback(async (id, claimed) => {
    const savedReward = await updateReward(uid, id, {
      claimed,
      claimedAt: claimed ? getCurrentTimestamp() : null,
    });
    setRewards((current) =>
      current.map((reward) => (reward.id === id ? savedReward : reward))
    );
  }, [uid]);

  return { rewards, loading, addReward, editReward, deleteReward, claimReward };
}
